import { useEffect, useId, useMemo, useState } from 'react';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { IconPlus } from '@shared/ui/icons';
import {
  apiErrorText,
  useGetEconProfitSplitQuery,
  useListEconDictQuery,
  useReplaceEconProfitSplitMutation,
} from '@shared/api';
import { FieldLabel, Notice, TextButton, VisuallyHidden, typo } from '@entities/fleet';
import {
  FlashBox,
  PctControl,
  PeriodControl,
  SumBar,
  SumLine,
  SplitTable,
  TableBox,
  Toolbar,
} from './styles';
import { type SplitRow, weightToPct } from './types';

/** «2026-10» → «октябрь 2026». */
function monthTitle(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

/** Форма коэффициентов распределения прибыли (Р-08). Для выбранного периода
 *  показывает сохранённые доли и сохраняет период целиком одним запросом:
 *  объекты, убранные из списка, из распределения выходят. Σ долей = 100%
 *  проверяют и форма, и сервер. Без права записи — только просмотр. */
export function ProfitSplitForm({ canWrite }: { canWrite: boolean }) {
  const uid = useId();
  const [period, setPeriod] = useState('');
  const [rows, setRows] = useState<SplitRow[]>([{ objectId: '', pct: '' }]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const periodValid = /^\d{4}-\d{2}$/.test(period);
  const { data: objData } = useListEconDictQuery({ dict: 'objects', active: true });
  const objects = (objData?.items ?? []) as Array<Record<string, unknown>>;
  const { data: saved, isFetching: loadingSaved } = useGetEconProfitSplitQuery(period, {
    skip: !periodValid,
  });
  const [replaceSplit, { isLoading: creating }] = useReplaceEconProfitSplitMutation();

  // Сохранённые доли периода — в форму, один раз на период: повторная загрузка
  // после сохранения не затирает то, что экономист уже правит.
  useEffect(() => {
    if (!saved || saved.period_month === loadedFor) return;
    setLoadedFor(saved.period_month);
    setRows(
      saved.items.length > 0
        ? saved.items.map((it) => ({ objectId: it.object_id, pct: weightToPct(it.weight) }))
        : [{ objectId: '', pct: '' }],
    );
  }, [saved, loadedFor]);

  const sumPct = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.pct.replace(',', '.')) || 0), 0),
    [rows],
  );
  const sumOk = Math.abs(sumPct - 100) < 0.01;

  function setRow(i: number, patch: Partial<SplitRow>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((rs) => [...rs, { objectId: '', pct: '' }]);
  }
  function removeRow(i: number) {
    setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));
  }

  async function submit() {
    setErr(null);
    setOk(null);
    if (!periodValid) {
      setErr('Укажите период (месяц и год).');
      return;
    }
    if (rows.some((r) => !r.objectId)) {
      setErr('В каждой строке выберите объект.');
      return;
    }
    const ids = rows.map((r) => r.objectId);
    if (new Set(ids).size !== ids.length) {
      setErr('Объекты в строках не должны повторяться.');
      return;
    }
    if (!sumOk) {
      setErr(`Сумма долей ${sumPct.toLocaleString('ru-RU')}% — должна быть ровно 100%.`);
      return;
    }
    try {
      const res = await replaceSplit({
        period,
        items: rows.map((r) => ({
          object_id: r.objectId,
          weight: Number(r.pct.replace(',', '.')) / 100,
        })),
      }).unwrap();
      setOk(
        `Распределение за ${monthTitle(period)} сохранено: объектов — ${res.items.length}. Объекты, которых нет в списке, из распределения убраны.`,
      );
    } catch (e) {
      setErr(apiErrorText(e, 'Не удалось сохранить распределение.'));
    }
  }

  const objectName = (o: Record<string, unknown>) =>
    (typeof o.name_canonical === 'string' && o.name_canonical) ||
    (typeof o.code === 'string' && o.code) ||
    String(o.id).slice(0, 8);

  return (
    <>
      <Toolbar>
        <FieldLabel htmlFor={`${uid}-period`}>Период</FieldLabel>
        <PeriodControl
          id={`${uid}-period`}
          type="month"
          value={period}
          onChange={(e) => {
            setPeriod(e.target.value);
            setOk(null);
            setErr(null);
          }}
        />
        <p className="count" aria-live="polite">
          {!periodValid
            ? 'Выберите месяц — сохранённые доли появятся ниже'
            : loadingSaved
              ? 'Загружаю сохранённые доли…'
              : saved && saved.items.length > 0
                ? `Сохранено: объектов — ${saved.items.length}, сумма ${weightToPct(saved.weight_sum)} %`
                : `За ${monthTitle(period)} распределения ещё нет`}
        </p>
      </Toolbar>

      {!canWrite && (
        <FlashBox>
          <Notice $tone="info" role="note">
            <span className="text">
              {typo('Только просмотр: доли меняет сотрудник с правом записи в Экономисте.')}
            </span>
          </Notice>
        </FlashBox>
      )}
      {objects.length === 0 && (
        <FlashBox>
          <Notice $tone="warning" role="note">
            <span className="text">
              {typo('Сначала заведите объекты во вкладке «Объекты» — доли распределяются по ним.')}
            </span>
          </Notice>
        </FlashBox>
      )}

      {periodValid && (
        <>
          <TableBox>
            <SplitTable>
              <thead>
                <tr>
                  <th scope="col">Объект</th>
                  <th scope="col" className="num">
                    Доля, %
                  </th>
                  {canWrite && (
                    <th scope="col">
                      <VisuallyHidden>Действия</VisuallyHidden>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <Select
                        id={`${uid}-obj-${i}`}
                        aria-label={`Объект, строка ${i + 1}`}
                        value={r.objectId}
                        disabled={!canWrite}
                        onChange={(e) => setRow(i, { objectId: e.target.value })}
                      >
                        <option value="" disabled>
                          — выберите объект —
                        </option>
                        {objects.map((o) => (
                          <option key={String(o.id)} value={String(o.id)}>
                            {objectName(o)}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="num">
                      <PctControl
                        id={`${uid}-pct-${i}`}
                        aria-label={`Доля, %, строка ${i + 1}`}
                        inputMode="decimal"
                        placeholder="25"
                        value={r.pct}
                        readOnly={!canWrite}
                        onChange={(e) => setRow(i, { pct: e.target.value })}
                      />
                    </td>
                    {canWrite && (
                      <td className="status">
                        <TextButton
                          type="button"
                          disabled={rows.length === 1}
                          aria-label={`Убрать строку ${i + 1}`}
                          onClick={() => removeRow(i)}
                        >
                          Убрать
                        </TextButton>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </SplitTable>
          </TableBox>

          <SumBar>
            {canWrite && (
              <TextButton type="button" onClick={addRow}>
                <IconPlus size={14} /> Добавить объект
              </TextButton>
            )}
            <span className="spacer" />
            <SumLine $ok={sumOk} aria-live="polite">
              Сумма долей: {sumPct.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %
              {sumOk ? ' — ровно 100 %' : ' — нужно 100 %'}
            </SumLine>
            {canWrite && (
              <Button
                type="button"
                $variant="primary"
                onClick={() => void submit()}
                disabled={creating || !sumOk}
              >
                {creating ? 'Сохраняю…' : 'Сохранить распределение'}
              </Button>
            )}
          </SumBar>
        </>
      )}

      {err && (
        <FlashBox>
          <Notice $tone="danger" role="alert">
            <span className="text">{typo(err)}</span>
          </Notice>
        </FlashBox>
      )}
      {ok && (
        <FlashBox>
          <Notice $tone="success" role="status">
            <span className="text">{typo(ok)}</span>
          </Notice>
        </FlashBox>
      )}
    </>
  );
}
